/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, ReactChild } from 'react';
import { Waypoint } from 'react-waypoint';
import { withOktaAuth } from '@okta/okta-react';
import { OktaAuth, AuthState } from '@okta/okta-auth-js';
import { RouteComponentProps } from 'react-router-dom';
import Media from 'react-media';
import { get, getText, cn, isNumber, isBoolean, getConfig, validInternalUser, superScriptSanitizer, getFlagType, isInternalUser } from '../../helpers';
import Breadcrumb from '../../components/breadcrumb';
import HelmetManager from '../../components/_common/helmet-manager';
import SPGVariables from '../../utilities/spg-variables';
import queryStringManager from '../../utilities/query-string-manager';
import HtmlContent from '../../components/_common/html-content';
import LinkListGroup from '../../components/_common/link-list-group';
import Image from '../../components/_common/image';
import DataFlag from '../../components/data-flag';
import Bookmark from '../../components/_common/bookmark';
import buildImageUrl from '../../utilities/build-image-url';
import Loader from '../../components/_common/loader';
import RelatedDatasetsAndSolutions from '../../components/blueprint-related-tiles';
import Note from '../../components/_common/note';
import RequestTrialButton from '../../components/request-a-trial-button';
import SubNavigation from '../../components/sub-nav';
import APIManager from '../../utilities/api-manager';
import SectionTitle from '../../components/_common/section-title';
import { ReactComponent as OverviewIcon } from '../../assets/svg/icons/overview.svg';
import smoothScroll from '../../utilities/smooth-scroll';
import VideoEmbed from '../../components/_common/video-embed/video-embed';
import TableGridOverlay from '../../components/table-grid-overlay';
import BluePrintVisualizationSection from '../../components/blueprint-visualuzation-section';
import VisualizationAuth from '../../components/_common/visualization-auth';
import ChatiqSearch from '../../components/chatiq-search';
import ReleaseNote from '../../components/release-note';

const bookmarkTooltip = getText('bookmark.tooltip.blueprint') as BookmarkTooltip[];
const metaDataText = getText('pages.blueprint.metaData');
const subNavigationItems = getText('pages.blueprint.subNavItems');
const chatiqBlueprintSearchText = getText('pages.llm.blueprint.searchQText');
const chatiqBlueprintPlaceholder = getText('pages.llm.blueprint.placeholder');
const spgVariables = SPGVariables();
let isUserOriginEnabled = false;
if (spgVariables.ENV !== 'dev') {
    isUserOriginEnabled = getConfig('responseHeader.isResHeaderEnabled');
}

const keyInformationMetaData = getText('pages.blueprint.keyInformationMetaData');
const vizTitle = getText('generic.requestThisVisualization');
function isNotNull<T>(value: T | null): value is T {
    return value !== null;
}
function getFileTypesDisplay(fileTypeObjects: {MarketplaceSolutionFileType: {KeyMarketplaceSolutionFileType: string; MktplaceSolutionFileType: string}}[]): string | null {

    const fileTypes = fileTypeObjects.map(fileType => fileType.MarketplaceSolutionFileType);
    return fileTypes.some(Boolean)
        ? fileTypes
            .filter(isNotNull)
            .map(fileType => fileType.MktplaceSolutionFileType)
            .join(', ')
        : null;
}
const keyInformationMetaFields = [
    {
        key: 'MarketplaceBlueprintInputFileTypes',
        name: keyInformationMetaData.input,
        value: (blueprint: BluePrintProfile) => {
            const fileTypes = blueprint.MarketplaceBlueprintInputFileTypes;
            return fileTypes && getFileTypesDisplay(fileTypes);
        }
    },
    {
        key: 'MarketplaceBlueprintOutputFileTypes',
        name: keyInformationMetaData.output,
        value: (blueprint: BluePrintProfile) => {
            const fileTypes = blueprint.MarketplaceBlueprintOutputFileTypes;
            return fileTypes && getFileTypesDisplay(fileTypes);
        }
    },
    {
        key: 'MarketplaceBlueprintOtherRequirements',
        name: keyInformationMetaData.otherRequirements,
        value: (blueprint: BluePrintProfile) => {
            const reqs = blueprint.MarketplaceBlueprintOtherRequirements || null;
            return reqs ? reqs.map(r => r.MarketplaceOtherRequirement || undefined).filter(el => !!el).join(', ') : null;
        }
    }
];

const setTableauUrl = (dataVisualization: MarketplaceBlueprintVisualization[]) => {
    const dataVisualizations: MarketDatasetVisualization[] = [];
    dataVisualizations.push(
        ...dataVisualization
            .sort((a, b) => {
                return a.FormOrder > b.FormOrder ? 1 : -1;
            })
            .map(i => {
                const mktplaceDataVisualizationURL = `t/${
                    i.VisualizationUrl ? spgVariables.TABLEAU_PUBLICSITE : spgVariables.TABLEAU_SECURESITE
                }/views/${i.VisualizationUrl}`;
                return {
                    MktplaceDataVisualizationTitle: i.VisualizationTitle,
                    MktplaceDataVisualizationURL: mktplaceDataVisualizationURL,
                    MktplaceShowcaseVisualization: i.MktplaceShowcaseVisualization,
                    FormOrder: i.FormOrder,
                    MarketplaceVizFootnote: { MarketplaceVizFootnote: i.MarketplaceVizFootNote },
                    MarketplaceVizLegalAttr: { MarketplaceVizLegalAttribution: i.MarketplaceVizLegalAttribution },
                    VisualizationType: i.VisualizationType,
                    ReportId: i.ReportId
                };
            })
    );
    return dataVisualizations;
};

const langCode = getText('langCode');

type OktaAuthProps = { oktaAuth: OktaAuth; authState: AuthState; _onAuthRequired: (oktaAuth: OktaAuth) => Promise<void> | void };

type BlueprintComponentProps = PageProps & BlueprintProps & BlueprintActions & FeatureFlags;

const Blueprint = (props: BlueprintComponentProps & OktaAuthProps & RouteComponentProps) => {
    const { authState, blueprint, blueprintId, fetchFullBlueprint, toggleSessionCreate, user, fflagEnableWorkbench } = props;
    if (!blueprint && blueprintId) fetchFullBlueprint({ id: blueprintId });
    const [productAccessLinks, setProductAccessLinks] = useState<ProductAccessLink[]>([]);
    const ratButtonElement = React.useRef<HTMLInputElement | null>(null);
    const [sticky, setSticky] = useState(false);
    const [showRatButton, setShowRatButton] = useState(true);
    const [isPendingOktaSignIn, setIsPendingOktaSignIn] = useState(false);
    const [isIpad, setIsIpad] = useState(false);

    const blueprintWrapperElement = useRef<HTMLInputElement | null>(null);

    const handleOktaSignIn = (): void => {
        const { history, oktaAuth } = props;
        oktaAuth.options.redirectUri = `${window.location.origin}/${langCode}/blueprints/demos/auth/callback`;
        // Ensure only one request to sign in is kicked off
        if (isPendingOktaSignIn) return;
        setIsPendingOktaSignIn(true);

        const fromUri = history.createHref(history.location);
        oktaAuth.signInWithRedirect({ originalUri: fromUri, idp: spgVariables.OKTA_IDP_ID });
    };

    // Handle re-authenticating via okta if the user has a token that has since expired
    const handleUnauthorizedResponse = (): void => {
        if (user.isLoggedIn && authState.isAuthenticated && !authState.isPending) {
            handleOktaSignIn();
        }
    };

    const [isChromeIncognitoMode, setIsChromeIncognitoMode] = useState(false);

    const isChromeIncognitoModeDetect = async () => {
        const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
        if (isChrome && navigator.storage && navigator.storage.estimate) {
            const w = window as any;
            const storageInfo = await navigator.storage.estimate();
            const heapSizeLimit = w.performance.memory.jsHeapSizeLimit;
            // (storageInfo.quota < heapSizeLimit) in incognito mode
            return storageInfo.quota && storageInfo.quota < heapSizeLimit;
        }
        return false;
    };

    isChromeIncognitoModeDetect().then(res => {
        setIsChromeIncognitoMode(!!res);
    });

    const setStickyRATButton = () => {
        if (ratButtonElement.current && ratButtonElement.current.parentElement && blueprintWrapperElement && blueprintWrapperElement.current) {
            const ratButtonRect = ratButtonElement.current.parentElement.getBoundingClientRect();
            const blueprintWrapperRect = blueprintWrapperElement.current.getBoundingClientRect();

            // Using top of screen as marker, calculate how far for the bottom of the button to reach the bottom of the screen:
            // 55 is height of button
            const buttonPastBottom = ratButtonRect.top + 55 - window.innerHeight;
            // We use the bottom of the solution wrapper to check if the footer is coming into view
            const blueprintPastBottom = blueprintWrapperRect.top + blueprintWrapperRect.height - window.innerHeight;
            // Make sure it doesn't work for desktop sizes
            const isDesktop = window.innerWidth >= 1024;
            //  Hide the button completely when footer is in view (avoids jumping issue)
            //  Set to sticky once the button has passed the screen by 20px, for seamless transition
            if (blueprintPastBottom < -20 && !isDesktop) {
                // hide the button when the footer comes into view
                setSticky(true);
                setShowRatButton(false);
            } else if (buttonPastBottom < -20 && !isDesktop) {
                //  Make sticky once button has fully entered view
                setSticky(true);
                setShowRatButton(true);
            } else {
                setSticky(false);
                setShowRatButton(true);
            }
        }
    };

    const getHideDetailTab = () => {
        if (blueprint) {
            if ((blueprint.MarketplaceBlueprintInputFileTypes && blueprint.MarketplaceBlueprintInputFileTypes.length > 0) ||
                (blueprint.MarketplaceBlueprintOutputFileTypes && blueprint.MarketplaceBlueprintOutputFileTypes.length > 0) ||
                (blueprint.MarketplaceBlueprintOtherRequirements && blueprint.MarketplaceBlueprintOtherRequirements.length > 0)) return false;
        }
        return true;
    };

    const getHideRequiredDatasetSolutionTab = () => {
        if (blueprint) {
            if (blueprint.MarketPlaceBlueprintDatasetSolution && blueprint.MarketPlaceBlueprintDatasetSolution.length > 0) return false;
        }
        return true;
    };

    const getHideOverviewTab = () => {
        if (blueprint) {
            if (
                blueprint.VidyardVideoID ||
                blueprint.MarketplaceBlueprintDefaultImage ||
                blueprint.MarketplaceBlueprintDemoId !== null ||
                blueprint.MarketplaceBlueprintVisualization.length > 0 ||
                blueprint.PlotlySampleAppPath ||
                (blueprint.MarketplaceBlueprintNotebooks && blueprint.MarketplaceBlueprintNotebooks.length > 0)
            ) return false;
        }
        return true;
    };

    const hasDemo = (): boolean => {
        let count = 0;
        const isImageOrVideoExists = blueprint ? (blueprint.VidyardVideoID || blueprint.MarketplaceBlueprintDefaultImage) : false;
        if (blueprint) {
            if (blueprint.MarketplaceBlueprintNotebooks && blueprint.MarketplaceBlueprintNotebooks.length) {
                count++;
            }
            if (blueprint.MarketplaceBlueprintVisualization !== null && blueprint.MarketplaceBlueprintVisualization.length > 0) {
                count++;
            }
            if (isImageOrVideoExists && count < 2) {
                count++;
            }
            if (blueprint.MarketplaceBlueprintDemoId !== null && count < 2) {
                return true;
            }
            return false;
        }
        return false;
    };

    const checkIfTablet = () => {
        const { maxTouchPoints, userAgent } = navigator;
        const { innerWidth, innerHeight } = window;

        if (
            /iPad|iPhone/.test(userAgent) ||
            (userAgent.includes('Macintosh') && maxTouchPoints > 0) ||
            /Android/.test(userAgent) ||
            (userAgent.includes('Linux armv81') && 'ontouchstart' in window && innerWidth > innerHeight && innerHeight < 768)
        ) {
            setIsIpad(true);
        } else setIsIpad(false);
    };

    const handleResize = () => {
        checkIfTablet();
    };

    useEffect(() => {
        checkIfTablet();
    }, []);

    useEffect(() => {
        if (user.userInfo && user.userInfo.InternalUser && fflagEnableWorkbench) {
            APIManager.getBlueprintAccessLinksbyKey(blueprintId).then(response => {
                if (response) setProductAccessLinks(response.data);
            });
        }
        if (blueprint && blueprint.MarketplaceBlueprintDemoId != null &&
            blueprint.MarketplaceBlueprintDemoId !== 'chart-iq' && hasDemo() &&
            user.isLoggedIn &&
            authState.isAuthenticated === false &&
            !authState.isPending &&
            authState.error == null
        ) {
            handleOktaSignIn();
        }

        window.addEventListener('scroll', setStickyRATButton, true);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', setStickyRATButton, false);
            window.removeEventListener('resize', handleResize);
        };
    }, [user, authState, blueprint]);

    if (!blueprint) return <Loader minHeight="50vh" />;

    if (blueprint) {
        const hideDetailsWaypointLink = getHideDetailTab();
        const hideRequiredWaypointLink = getHideRequiredDatasetSolutionTab();
        const hideOverviewWaypointLink = getHideOverviewTab();
        subNavigationItems.forEach((item: AnonymousObject) => {
            if (item.id === 0) {
                item.enabled = !hideOverviewWaypointLink;
            }
            if (item.id === 1) {
                item.enabled = !hideDetailsWaypointLink;
            }
            if (item.id === 2) {
                item.enabled = !hideRequiredWaypointLink;
            }
        });
    }

    const blueprintUrl = `/blueprints/${get(props, 'match.params.id', '')}`;
    const metaData = {
        title: `${blueprint.MarketplaceBlueprintName} ${metaDataText.subTitle}`,
        description: blueprint.MarketplaceBlueprintShortDesc,
        type: 'Blueprint',
        url: blueprintUrl,
        image: buildImageUrl(blueprint.SocialSharingImageFilePath)
    };

    const breadCrumbData = [
        { text: getText('generic.blueprints'), url: '/blueprints' },
        { text: (blueprint && blueprint.MarketplaceBlueprintName) || '', url: blueprintUrl }
    ];

    const blueprintDocURLList = [];

    if (blueprint.MarketplaceBlueprintSupportDocs) {
        blueprintDocURLList.push(
            ...blueprint.MarketplaceBlueprintSupportDocs.sort((a, b) => {
                return a.FormOrder - b.FormOrder;
            })
                .filter(item => item.MarketPlaceSupportDoc !== null)
                .map(item => {
                    return {
                        text: `${item.MarketPlaceSupportDoc.MarketPlaceSupportDocument}`,
                        type: 'externalLink',
                        url: item.MarketPlaceSupportDoc.WebSiteURL
                    };
                })
        );
    }

    if (blueprint.MarketplaceBlueprintSupportEmail) {
        blueprintDocURLList.push({
            text: blueprint.MarketplaceBlueprintSupportEmail,
            type: 'emailAddress',
            url: `mailto:${blueprint.MarketplaceBlueprintSupportEmail}`
        });
    } else {
        blueprintDocURLList.push({ text: getText('generic.supportEmail'), type: 'emailAddress', url: `mailto:${getText('generic.supportEmail')}` });
    }

    let researchLinks: LinkListGroupItem[] | [] = [];

    const researchData =
        blueprint.MarketplaceBlueprintResearches &&
        blueprint.MarketplaceBlueprintResearches.sort((a, b) => {
            return a.FormOrder > b.FormOrder ? 1 : -1;
        });

    if (researchData) {
        researchLinks = researchData.map(researchDetails => {
            const linkListGroupItem: LinkListGroupItem = {
                text: researchDetails.BlueprintResearchTitle,
                type: '',
                url: researchDetails.WebSiteURL
            };
            return linkListGroupItem;
        });
    } else {
        researchLinks = [];
    }

    const internalResourceLinks: LinkListGroupItem[] = [];

    if (productAccessLinks && productAccessLinks.length > 0) {
        productAccessLinks
            .filter(x => x.KeyMarketplaceBlueprint === blueprintId)
            .sort((a, b) => a.FormOrder - b.FormOrder)
            .forEach(item => {
                internalResourceLinks.push({
                    text: item.MarketplaceAccessLinkTitle,
                    type: 'externalLink',
                    url: item.WebSiteURL
                });
            });
    }

    const handleBlur = (w: Waypoint.CallbackArgs, hash = '') => {
        const focusedElement = document.activeElement;
        if (focusedElement && focusedElement.tagName === 'A') {
            const focusedAnchorElement = (focusedElement as HTMLAnchorElement);
            if (hash === focusedAnchorElement.hash) {
                focusedAnchorElement.blur();
            }
        }
    };

    const handleScroll = (w: Waypoint.CallbackArgs, hash = '') => {
        if (!w.previousPosition || w.viewportBottom < w.waypointTop) return;
        queryStringManager.setHash(hash);
    };

    const renderLogo = () => {
        if (blueprint.MarketplaceBlueprintLogos) {
            return blueprint.MarketplaceBlueprintLogos.sort(
                (a: MarketBlueprintLogos, b: MarketBlueprintLogos) => a.FormOrder - b.FormOrder
            ).map((Logo: MarketBlueprintLogos) => <Image key={Logo.FormOrder} src={Logo.OnlineImageFilePath || '/'} alt={Logo.DataProvider || ''} />);
        }
        return null;
    };

    const constructMetaFields = () => {
        return keyInformationMetaFields.map(field => {
            const value = blueprint && field.value(blueprint);
            return (
                value && (
                    <li className="definition-item" key={field.key}>
                        <strong className="definition-item__label">{field.name}</strong>
                        <span className="definition-item__text">
                            <HtmlContent html={value} />
                        </span>
                    </li>
                )
            );
        });
    };

    const scrollTo = (event: React.SyntheticEvent, hash: string) => {
        smoothScroll(hash, event);
        queryStringManager.setHash(hash);
    };

    const dataVisualization =
        blueprint.MarketplaceBlueprintVisualization !== null ? setTableauUrl(blueprint.MarketplaceBlueprintVisualization || []) : [];
    const hasSecureVisual = dataVisualization.filter(visual => visual.MktplaceShowcaseVisualization === false).length > 0;
    return (
        <article className="page blueprint" ref={blueprintWrapperElement}>
            <HelmetManager metaData={metaData} />
            {hasSecureVisual && (
                <VisualizationAuth
                    toggleSessionCreate={toggleSessionCreate}
                    isTableauSessionCreated={user.isTableauSessionCreated || false}
                    isLoggedIn={user.isLoggedIn}
                />
            )}
            <Breadcrumb pathData={breadCrumbData} sanitizeSuperScript noMargin />
            <Waypoint topOffset="20%" bottomOffset="79%" onEnter={(w: Waypoint.CallbackArgs) => handleScroll(w)}>
                <section className="page-section border-top border-bottom margin-bottom-0">
                    <div className="page-section__inner">
                        <div className="blueprint__header two-column-layout">
                            <div className="blueprint-summary two-column-layout__col">
                                <div
                                    className={
                                        getFlagType(
                                            blueprint.MarketplaceBlueprintDateAdded,
                                            blueprint.MarketplaceBlueprintEnhancementDate
                                        ).startsWith('default')
                                            ? 'blueprint-summary-row__same-row'
                                            : 'blueprint-summary-row'
                                    }
                                >
                                    <div className="blueprint-summary__logo">{renderLogo()}</div>
                                    <div className="blueprint-summary__overview-flag">
                                        <div className="blueprint-summary__overview-flag__group">
                                            <DataFlag
                                                datasetId={blueprint.KeyMarketplaceBlueprint}
                                                datasetName={blueprint.MarketplaceBlueprintName}
                                                datasetAddedDate={blueprint.MarketplaceBlueprintDateAdded}
                                                datasetEnhancementDate={blueprint.MarketplaceBlueprintEnhancementDate}
                                                variant="profile"
                                                notifyVariant="blueprint"
                                            />
                                            <Bookmark
                                                isLoggedIn={user.isLoggedIn}
                                                bookmarkTooltip={bookmarkTooltip}
                                                keyId={blueprintId}
                                                bookmarkType="blueprints"
                                                longBookmarkIcon
                                            />
                                        </div>
                                    </div>
                                </div>
                                <h1>{superScriptSanitizer(blueprint.MarketplaceBlueprintName)}</h1>
                                {blueprint.MarketplaceBlueprintAlertActive !== null &&
                                    blueprint.MarketplaceBlueprintAlertActive &&
                                    blueprint.MarketplaceBlueprintAlertTitle &&
                                    blueprint.MarketplaceBlueprintAlert && (
                                    <Note title={blueprint.MarketplaceBlueprintAlertTitle} noteParagraph={blueprint.MarketplaceBlueprintAlert} />
                                )}
                                {blueprint.MarketplaceBlueprintAlertActive !== null &&
                                    !blueprint.MarketplaceBlueprintAlertActive &&
                                    blueprint.MarketplaceBlueprintEnhancementDesc &&
                                    blueprint.MarketplaceBlueprintEnhancementDate && (
                                    <ReleaseNote
                                        title={getText('generic.releaseNotes')}
                                        noteParagraph={blueprint.MarketplaceBlueprintEnhancementDesc}
                                        linkText={getText('generic.readMore')}
                                        linkHref={blueprint.MarketplaceBlueprintEnhancementLink}
                                        dateAdded={blueprint.MarketplaceBlueprintDateAdded}
                                        dateUpdated={blueprint.MarketplaceBlueprintEnhancementDate}
                                    />
                                )}
                                {!isIpad && (
                                    <div className="chatiq-search-visibility">
                                        <ChatiqSearch
                                            text={chatiqBlueprintSearchText}
                                            placeholder={chatiqBlueprintPlaceholder}
                                            variant="blueprint"
                                            id={blueprint.KeyMarketplaceBlueprint}
                                            isInternal={validInternalUser(user)}
                                            dataProfile={blueprint}
                                            isLoggedIn={user.isLoggedIn}
                                        />
                                    </div>
                                )}
                                <HtmlContent html={blueprint.MarketplaceBlueprintLongDesc} className="blueprint-summary__description" />
                                <div className="blueprint-provider">
                                    <h2 className="subheading subheading--with-border">{getText('pages.blueprint.serviceProviderInformation')}</h2>
                                    <HtmlContent html={blueprint.MarketplaceBlueprintDataProviderDesc} />
                                </div>
                            </div>
                            <aside className="blueprint-materials two-column-layout__col">
                                <div
                                    ref={ratButtonElement}
                                    className={cn('blueprint__rat-button-wrapper', {
                                        'blueprint__rat-button-wrapper--sticky': sticky,
                                        'blueprint__rat-button-wrapper--hidden': !showRatButton
                                    })}
                                >
                                    <RequestTrialButton
                                        className="blueprint__rat-btn-text"
                                        buttonText={blueprint.MarketplaceTrialButtonTitle}
                                        dataProvider={
                                            blueprint.MarketplaceDataProviders && blueprint.MarketplaceDataProviders.length
                                                ? blueprint.MarketplaceDataProviders[0].MarketplaceDataProvider
                                                : ''
                                        }
                                        productName={blueprint.MarketplaceBlueprintName}
                                        productId={blueprint.KeyMarketplaceBlueprint}
                                        isLoggedIn={user.isLoggedIn}
                                        productVariant="blueprint"
                                        dataFilePath={blueprint.MSADataFilePath}
                                        marketMSADataLink={blueprint.MSADataLink}
                                        marketplaceMSATooltip={blueprint.MarketplaceMSATooltip}
                                        metaData={metaData}
                                    />
                                </div>
                                <Media query={{ maxWidth: 1023 }}>
                                    <div className="blueprint__support-links">
                                        {blueprintDocURLList && blueprintDocURLList.length > 0 && (
                                            <LinkListGroup list={blueprintDocURLList} title={getText('generic.supportTitle')} isAccordion />
                                        )}
                                        {researchLinks && researchLinks.length > 0 && (
                                            <LinkListGroup list={researchLinks} title={getText('generic.researchTitle')} isAccordion />
                                        )}
                                        {user.isLoggedIn && internalResourceLinks && internalResourceLinks.length > 0 && (
                                            <LinkListGroup list={internalResourceLinks} title={getText('generic.internalResources')} isAccordion />
                                        )}
                                    </div>
                                </Media>
                                <Media query={{ minWidth: 1024 }}>
                                    <div className="blueprint__support-links">
                                        {blueprintDocURLList && blueprintDocURLList.length > 0 && (
                                            <LinkListGroup list={blueprintDocURLList} title={getText('generic.supportTitle')} />
                                        )}
                                        {researchLinks && researchLinks.length > 0 && (
                                            <LinkListGroup list={researchLinks} title={getText('generic.researchTitle')} />
                                        )}
                                        {user.isLoggedIn && internalResourceLinks && internalResourceLinks.length > 0 && (
                                            <LinkListGroup list={internalResourceLinks} title={getText('generic.internalResources')} />
                                        )}
                                    </div>
                                </Media>
                            </aside>
                        </div>
                    </div>
                </section>
            </Waypoint>

            <div className="blueprint-content">
                <SubNavigation navItems={subNavigationItems.filter((item: AnonymousObject) => item.enabled)} />
                {!getHideOverviewTab() && (
                    <Waypoint
                        topOffset="20%"
                        bottomOffset="79%"
                        onEnter={(w: Waypoint.CallbackArgs) => {
                            handleScroll(w, subNavigationItems[0].href);
                        }}
                        onLeave={(w: Waypoint.CallbackArgs) => {
                            handleBlur(w, subNavigationItems[0].href);
                        }}
                    >
                        <section id="overview" className="page-section blueprint-detail">
                            <BluePrintVisualizationSection
                                isTableauSessionCreated={user.isTableauSessionCreated || false}
                                isBundle={false}
                                isLoggedIn={user.isLoggedIn}
                                dataSectionType="data-visualization"
                                dataVisualization={dataVisualization}
                                MarketplaceBlueprintDefaultImage={blueprint.MarketplaceBlueprintDefaultImage}
                                VidyardVideoID={blueprint.VidyardVideoID}
                                scrollTo={event => handleScroll(event)}
                                accessToken={authState.accessToken ? authState.accessToken.accessToken : null}
                                isChromeIncognitoMode={isChromeIncognitoMode}
                                onUnauthorizedResponse={handleUnauthorizedResponse}
                                PlotlySampleAppPath={blueprint.PlotlySampleAppPath}
                                isPendingOktaSignIn={isPendingOktaSignIn}
                                MarketplaceBlueprintName={blueprint.MarketplaceBlueprintName}
                                MarketplaceBlueprintDemoId={blueprint.MarketplaceBlueprintDemoId}
                                dataNotebooks={blueprint.MarketplaceBlueprintNotebooks}
                            />
                            <hr />
                        </section>
                    </Waypoint>
                )}

                {!getHideDetailTab() && (
                    <Waypoint
                        topOffset="20%"
                        bottomOffset="79%"
                        onEnter={(w: Waypoint.CallbackArgs) => {
                            handleScroll(w, subNavigationItems[1].href);
                        }}
                        onLeave={(w: Waypoint.CallbackArgs) => {
                            handleBlur(w, subNavigationItems[1].href);
                        }}
                    >
                        <section
                            id="details"
                            className={`page-section blueprint__details ${getHideOverviewTab() ? 'blueprint__details__first' : ''}`}
                        >
                            <div className="page-section__inner">
                                {keyInformationMetaFields.some(item => {
                                    return item.value(blueprint) !== null;
                                }) && (
                                    <div className="blueprint__column">
                                        <SectionTitle
                                            hash="details"
                                            text={keyInformationMetaData.details}
                                            onClick={event => scrollTo(event, '#details')}
                                        />
                                        <ul className="solution-metadata">{constructMetaFields()}</ul>
                                    </div>
                                )}
                            </div>
                            <hr />
                        </section>
                    </Waypoint>
                )}
                {!getHideRequiredDatasetSolutionTab() && (
                    <Waypoint
                        topOffset="20%"
                        bottomOffset="79%"
                        onEnter={(w: Waypoint.CallbackArgs) => handleScroll(w, subNavigationItems[2].href)}
                        onLeave={(w: Waypoint.CallbackArgs) => handleBlur(w, subNavigationItems[2].href)}
                    >
                        <section id="required-datasets-solutions" className="page-section">
                            {blueprint.MarketPlaceBlueprintDatasetSolution && blueprint.MarketPlaceBlueprintDatasetSolution.length > 0 && (
                                <RelatedDatasetsAndSolutions
                                    scrollTo={event => scrollTo(event, '#required-datasets-solutions')}
                                    className="dataset__related-items"
                                    // blueprintRelatedTiles={blueprint.MarketPlaceBlueprintDatasetSolution}
                                    blueprintRelatedTiles={blueprint}
                                />
                            )}
                        </section>
                    </Waypoint>
                )}
            </div>

            <Media query={{ minWidth: 1024 }}>
                <div>
                    <Waypoint topOffset="20%" bottomOffset="79%" onEnter={(w: Waypoint.CallbackArgs) => handleScroll(w)}>
                        <section className="page-section">
                            <div className="page-section__inner">
                                <RequestTrialButton
                                    productName={blueprint.MarketplaceBlueprintName}
                                    productId={blueprint.KeyMarketplaceBlueprint}
                                    dataProvider={
                                        blueprint.MarketplaceDataProviders && blueprint.MarketplaceDataProviders.length
                                            ? blueprint.MarketplaceDataProviders[0].MarketplaceDataProvider
                                            : ''
                                    }
                                    buttonText={blueprint.MarketplaceTrialButtonTitle}
                                    isLoggedIn={user.isLoggedIn}
                                    productVariant="blueprint"
                                    isXLButton
                                    dataFilePath={blueprint.MSADataFilePath}
                                    marketMSADataLink={blueprint.MSADataLink}
                                    marketplaceMSATooltip={blueprint.MarketplaceMSATooltip}
                                    metaData={metaData}
                                />
                            </div>
                        </section>
                    </Waypoint>
                </div>
            </Media>
        </article>
    );
};

// TODO: The parameterization type of withOktaAuth is assumed to extend the interface of the props it injects.
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore
export default withOktaAuth<BlueprintProps & PageProps>(Blueprint);
